import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import type { SellerProjectThread } from "@recruiter/shared";

import { clearProjectFilterInUrl, projectFilterFromSearch } from "../../lib/project-filter";
import { projectsQueryOptions, SellerQueryError } from "../../lib/queries/seller";
import { deleteAllSellers } from "../../lib/seller-client";
import { SellerErrorState } from "./seller-error-state";

// The project-first picker: every concierge project (run) — LIVE ones first,
// then COMPLETED ones (a finished/archived project never drops off here, so
// talent keep access). Click a project to expand its talents (accordion); click
// a talent to drop straight into THAT project's thread. Server state via TanStack
// Query; "Refresh"/retry re-run it, "Delete all" wipes every project's test data.
export function ProjectDirectory({
  onPickSeller,
}: {
  onPickSeller: (sellerId: string, threadId: string) => void;
}) {
  const { data, isPending, error, refetch } = useQuery(projectsQueryOptions());
  const [projectFilter, setProjectFilter] = useState<string | null>(() =>
    typeof window !== "undefined" ? projectFilterFromSearch(window.location.search) : null,
  );
  const allProjects = data?.projects ?? [];
  const projects = projectFilter
    ? allProjects.filter((p) => p.project_id === projectFilter)
    : allProjects;
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [wiping, setWiping] = useState(false);
  const [wipeError, setWipeError] = useState<string | null>(null);

  // When the client debug button deep-links us to one project, open its (usually
  // sole) run once so the tester lands on the talent list, not a collapsed row.
  // A ref keeps it to ONE shot — re-collapsing a run must not spring back open.
  const autoExpandedRef = useRef(false);
  useEffect(() => {
    if (!autoExpandedRef.current && projectFilter && projects.length > 0) {
      autoExpandedRef.current = true;
      setExpandedRunId(projects[0].run_id);
    }
  }, [projectFilter, projects]);

  function showAllProjects(): void {
    clearProjectFilterInUrl();
    setProjectFilter(null);
  }

  async function wipeAll(): Promise<void> {
    if (
      !window.confirm(
        "Delete ALL talent test data: every run, thread, message and offer " +
          "across all projects? This empties the picker and cannot be undone.",
      )
    ) {
      return;
    }
    setWiping(true);
    setWipeError(null);
    const out = await deleteAllSellers();
    setWiping(false);
    if (out.ok) void refetch();
    else setWipeError(out.message);
  }

  if (isPending) {
    return (
      <div className="sl-center">
        <div className="sl-empty">Loading projects…</div>
      </div>
    );
  }
  if (error) {
    return (
      <SellerErrorState
        title="Couldn't load projects"
        message={error instanceof SellerQueryError ? error.message : "Something went wrong."}
        onRetry={() => void refetch()}
      />
    );
  }
  if (projects.length === 0) {
    return (
      <div className="sl-center">
        <div className="sl-empty">
          {projectFilter
            ? "No talent activity for this project yet. Talents appear here once the concierge has reached out."
            : "No projects yet. When a client approves outreach (or runs discovery), concierge projects appear here to simulate, and stay listed after they finish."}
          <div style={{ marginTop: "var(--rc-space-4)" }}>
            {projectFilter ? (
              <button className="sl-btn sl-btn--ghost" onClick={showAllProjects}>
                Show all projects
              </button>
            ) : (
              <button className="sl-btn sl-btn--ghost" onClick={() => void refetch()}>
                Refresh
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sl-directory">
      <div className="sl-directory__head">
        <div>
          <h2 className="sl-directory__title">Concierge projects</h2>
          <div className="sl-directory__hint">
            Active projects first, then completed ones. Open a project, then pick a talent to
            chat as them.
          </div>
        </div>
        <div className="sl-directory__actions">
          <button className="sl-btn sl-btn--ghost" onClick={() => void refetch()}>
            Refresh
          </button>
          <button
            className="sl-btn sl-btn--danger"
            onClick={() => void wipeAll()}
            disabled={wiping}
          >
            {wiping ? "Deleting…" : "Delete all"}
          </button>
          {wipeError ? <div className="sl-inline-error">{wipeError}</div> : null}
        </div>
      </div>
      {projectFilter ? (
        <div className="sl-directory__scope" role="status">
          <span className="sl-directory__scope-text">
            Showing one project (opened from the client debug button).
          </span>
          <button className="sl-btn sl-btn--ghost" onClick={showAllProjects}>
            Show all projects
          </button>
        </div>
      ) : null}
      <ul className="sl-project-list">
        {projects.map((p) => {
          const expanded = p.run_id === expandedRunId;
          const projectPill = p.closed
            ? { label: "Completed", cls: "sl-pill--done" }
            : { label: "Active", cls: "sl-pill--active" };
          return (
            <li key={p.run_id} className={`sl-project${p.closed ? " sl-project--closed" : ""}`}>
              <button
                className="sl-project__head"
                aria-expanded={expanded}
                onClick={() => setExpandedRunId(expanded ? null : p.run_id)}
              >
                <span className="sl-project__caret" aria-hidden="true">
                  {expanded ? "▾" : "▸"}
                </span>
                <span className="sl-project__body">
                  <span className="sl-project__name">{p.project_name || "Untitled project"}</span>
                  <span className="sl-project__sub">
                    {p.client_name} · {activityLabel(p.last_activity)}
                  </span>
                  <span className="sl-project__meta">{projectCounts(p)}</span>
                </span>
                <span className={`sl-pill sl-project__status ${projectPill.cls}`}>
                  {projectPill.label}
                </span>
              </button>
              {expanded ? (
                <ul className="sl-project__sellers">
                  {p.sellers.map((s) => {
                    const pill = statusPill(s);
                    return (
                      <li key={s.thread_id}>
                        <button
                          className="sl-seller-row"
                          onClick={() => onPickSeller(s.seller_id, s.thread_id)}
                        >
                          <span className="sl-seller-row__top">
                            <span className="sl-seller-row__id">{s.seller_id}</span>
                            <span className={`sl-pill ${pill.cls}`}>{pill.label}</span>
                          </span>
                          {s.last_message ? (
                            <span className="sl-seller-row__preview">{s.last_message}</span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// At-a-glance counts for a project header. Talent count always shows; the rest
// only when non-zero so the line stays scannable.
function projectCounts(p: {
  seller_count: number;
  awaiting_reply: number;
  offers: number;
  declined: number;
}): string {
  const parts = [`${p.seller_count} talent${p.seller_count === 1 ? "" : "s"}`];
  if (p.awaiting_reply > 0) parts.push(`${p.awaiting_reply} awaiting you`);
  if (p.offers > 0) parts.push(`${p.offers} offer${p.offers === 1 ? "" : "s"}`);
  if (p.declined > 0) parts.push(`${p.declined} declined`);
  return parts.join(" · ");
}

// Compact, locale-aware "when was this last active" label for the sort key.
function activityLabel(iso: string): string {
  if (!iso) return "no activity";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Per-seller status badge (mirrors the inbox's pill mapping).
function statusPill(s: SellerProjectThread): { label: string; cls: string } {
  if (s.has_offer || s.state === "offer_received") {
    return { label: "Offer sent", cls: "sl-pill--offer" };
  }
  if (s.state === "awaiting_seller" || s.state === "sent") {
    return { label: "Your reply", cls: "sl-pill--reply" };
  }
  if (s.state === "awaiting_buyer") {
    return { label: "With client", cls: "sl-pill--wait" };
  }
  if (s.state === "declined") {
    return { label: "Declined", cls: "sl-pill--declined" };
  }
  return { label: s.state, cls: "sl-pill--wait" };
}
